<template>
    <component
        :is="navbarComponent"
        :username="username"
        :current-vertical="currentVertical"
        :my-account-url="myAccountUrl"
        :main-links="mainLinks"
        :user-links="userLinks"
        :vertical-links="verticalLinks"
        :is-authenticated="isAuthenticated"
        :advertiser-id="advertiserId"
        :show-favorite="showFavorite">
    </component>
</template>

<script>
import DesktopNavbar from './advertiser-desktop-navbar';
import MobileNavbar from './advertiser-mobile-navbar';
import showMobile from '../../../mixins/show-mobile';
import {slugify} from '../../../utils.js';

export default {
    components: { DesktopNavbar, MobileNavbar },

    mixins: [ showMobile ],

    props: {
        currentVertical: String,
        verticals: Array,
        username: String,
        advertiserId: Number,
        isAuthenticated: Boolean,
        showBilling: Boolean,
        showCampaigns: Boolean,
        showReporting: Boolean,
        showUserPermissions: Boolean,
        showRatesEntry: Boolean,
        myAccountUrl: String,
        userPermissionsUrl: String,
        userProfileUrl: String,
        changePasswordUrl: String,
        logoutUrl: String,
        showPostLead: Boolean,
        userRoles: Array,
        currentUri: String,
        showUwmBrokerReportDownload: Boolean,
        showFavorite: Boolean
    },

    computed: {
        navbarComponent() {
            return this.showMobile ? 'mobile-navbar' : 'desktop-navbar';
        },
        mainLinks() {
            if (!this.advertiserId || this.verticals.length === 0 || String(this.currentVertical) === '') {
                return []
            }

            let areas = [];

            const inAVertical = this.isMortgage || this.isHomeEquity || this.isRealEstate || this.isDeposits;

            if (inAVertical) {
                areas.push({ label: 'Campaigns', url: this.dashboardUrl, selected: this.isCampaigns });
                areas.push({ label: 'Reporting', url: this.reportingUrl, selected: this.isReporting, hide: !this.showReporting });
            }

            if (this.featureFlag('amerisave-report-enabled')) {
              areas.push({
                  label: 'AmeriSave Report',
                  url: '/reports/amerisave',
                  selected: this.currentUriIs('reports/amerisave')
              });
            }

            if (this.featureFlag('interest-report-enabled') && inAVertical) {
              areas.push({
                  label: 'Interest Report',
                  url: '/reports/interest',
                  selected: this.currentUriIs('reports/interest')
              });
            }

            if (this.showUwmBrokerReportDownload) {
                areas.push({
                    label: 'UWM Brokers Report Download',
                    url: '/tools/brokers/status-report'
                });
            }

            if (this.showRatesEntry) {
                if (this.isHomeEquity) {
                    areas.push({label: 'Market Groups', url: this.marketGroupsUrl, selected: this.isMarketGroups});
                    areas.push({label: 'Rates', url: this.ratesEntryUrl, selected: this.isRatesEntry});
                }

                if (this.isDeposits) {
                    areas.push({label: 'Rates', url: this.ratesEntryUrl, selected: this.isRatesEntry});
                }
            }

            if (this.isMortgage || this.isHomeEquity || this.isConsumerReviews) {
                areas.push({label: 'Consumer Reviews', url: this.consumerReviewsUrl, selected: this.isConsumerReviews});
            }

            if (this.isMortgage && this.showPostLead) {
                areas.push({label: 'Post Lead Upload', url: this.postLeadUploadUrl, selected: this.isPostLead});
            }

            return areas;
        },
        verticalLinks() {
            const that = this;

            return this.verticals.map((vertical) => {
                return { label: vertical, url: that.verticalUrl(vertical), slug: slugify(vertical) }
            })
        },
        userLinks() {
            const navOptions = [];

            if (this.hasRole(['staff', 'engineer'])) {
                navOptions.push({ label: 'Admin Resources', url: '/manage/dashboards/main' })
            }

            if (this.hasRole(['editorial'])) {
                const ratesResearchUrl = `https://rates-research-portal${window.isEnvironment('prod') ? '' : '.qa'}.bankrate.com`;
                navOptions.push({label: 'Rates Research', url: ratesResearchUrl});
            }

            if (this.hasRole(['wealth-rates-management'])) {
                navOptions.push({ label: 'Wealth Rates Management', url: '/wealth/rates-management' })
            }

            if (this.hasRole(['loantek-management'])) {
                navOptions.push({ label: 'LoanTek', url: '/loantek' })
            }

            if (this.hasRole(['formula-editor', 'engineer'])) {
                navOptions.push({ label: 'Formula Editor', url: '/manage/resources/mainline-formula' })
            }

            if (this.hasRole('staff') && this.hasAdvertiserActive()) {
                navOptions.push({ label: 'Funds Manager', url: '/admin/funds-manager' });
                navOptions.push({ label: 'Creatives', url: '/campaigns/creatives' });
            }

            if (this.hasRole('staff')) {
                navOptions.push(
                    { label: 'Advertiser Onboarding', url: '/admin/advertisers/onboard' },
                    { label: 'Payment Plan Admin', url: '/admin/advertisers/payment-plan' },
                )
            }

            if (this.hasRole('engineer')) {
                navOptions.push(
                  { label: 'EP Test', url: '/admin/tools/ep-test' },
                  { label: 'User Info', url: '/admin/user-info' }
                )
            }

            if (this.hasRole(['staff', 'engineer'])) {
                navOptions.push(
                  { label: 'Bulk Tag Editor', url: '/tools/tags/bulk-assign' },
                  { label: 'Bulk Tag Audit', url: '/tools/tags/bulk-audit' },
                  { label: 'Bulk Attribute Editor', url: '/tools/attributes/bulk-assign' },
                  { label: 'Internal Tools & Help', url: '/tools' },
                  { label: 'Phone Number Provision', url: '/admin/advertisers/provisioning' },
                  { label: 'States for Advertisers', url: '/admin/advertisers/states' }
                )
            }

            return navOptions.concat([
                { label: 'Billing', url: this.billingSetupUrl, hide: !this.showBilling },
                { label: 'Change Password', url: this.changePasswordUrl },
                { label: 'User Profile', url: this.userProfileUrl },
                { label: 'Logout', url: this.logoutUrl },
            ]);
        },
        isCampaigns() {
            return window.location.href.includes('campaigns') && !this.isReporting && !this.isMarketGroups && !this.isRatesEntry && !this.isPostLead
        },
        isReporting() {
            return window.location.href.includes('reporting')
        },
        isMarketGroups() {
            return window.location.href.includes('market-groups')
        },
        isConsumerReviews() {
            return window.location.href.includes('consumer-reviews')
        },
        isPostLead() {
            return window.location.href.includes('post-lead')
        },
        isRatesEntry() {
            return window.location.href.includes('rates')
        },
        isHomeEquity() {
            return String(this.currentVertical) === 'home-equity';
        },
        isMortgage() {
            return String(this.currentVertical) === 'mortgage';
        },
        isDeposits() {
            return String(this.currentVertical) === 'deposits';
        },
        isRealEstate() {
            return String(this.currentVertical) === 'real-estate';
        },
        dashboardUrl() {
            return this.verticalUrl(this.currentVertical ? this.currentVertical : 'mortgage')
        },
        reportingUrl() {
            return this.verticalUrl(this.currentVertical ? this.currentVertical : 'mortgage') + '/reporting';
        },
        marketGroupsUrl() {
            return this.verticalUrl(this.currentVertical ? this.currentVertical : 'home-equity') + '/market-groups';
        },
        consumerReviewsUrl() {
            return `/${this.currentVertical}/consumer-reviews`;
        },
        postLeadUploadUrl() {
            const productCategorySlug = this.currentVertical ? this.currentVertical : 'mortgage'
            return `${route('post-lead.index', {productCategorySlug})}`
        },
        ratesEntryUrl() {
            return this.verticalUrl(this.currentVertical ? this.currentVertical : 'home-equity') + '/rates';
        },
        billingSetupUrl() {
            return route('billing-setup.setup.show')
        }
    },

    methods: {
        labelSort (arr) {
            return _.orderBy(arr, ['label'], ['desc'])
        },
        verticalUrl(vertical) {
            return route(`campaigns.${slugify(vertical)}.index`)
        },
        currentUriIs(uri) {
            return this.currentUri === uri;
        }
    }
}
</script>
